import { Action, ActionReducer } from '@ngrx/store';

import { forceLogout, logoutFailure, logoutSuccess } from './auth.actions';

/**
 * The actions that mark a session as torn down: `AuthEffects` clears the
 * tokens and routes to `/auth/login` in the same breath as each of them.
 * `logout` itself is *not* here — a user sign-out asks the backend first, and
 * emptying the store while that request is in flight would render a blank
 * shell over a still-valid session.
 */
const SIGN_OUT = new Set<string>([
  logoutSuccess.type,
  logoutFailure.type,
  forceLogout.type,
]);

/**
 * Signing out ends what the session loaded, not just its tokens (F-031).
 *
 * The way out is a route change inside a live SPA, so without this every
 * slice — the operator's own profile, the transaction list, the vaults —
 * outlived the session and was there for the next person to sign in on the
 * same browser. This root meta-reducer hands every slice `undefined` on a
 * sign-out, so each returns to its own initial state; a slice added later is
 * covered without touching it. Nothing in the store is meant to outlive a
 * session: language and the device id live in `localStorage`, behind their
 * own services.
 */
export function clearStateOnSignOut<S, A extends Action = Action>(
  reducer: ActionReducer<S, A>
): ActionReducer<S, A> {
  return (state, action) =>
    reducer(SIGN_OUT.has(action.type) ? undefined : state, action);
}
